import _ from "lodash";
import { createTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import components from "./Components";
import typography from "./Typography";
import { shadows, darkshadows } from "./Shadows";
import { baseDarkTheme, baseLightTheme } from "./Colors";
import * as locales from "@mui/material/locale";
import { getCookie } from "cookies-next";

const getThemeFromCookie = () => {
  const userPreference = getCookie("theme-preference");

  if (userPreference) {
    return userPreference;
  }

  if (typeof window !== "undefined") {
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    return prefersDark ? "dark" : "light";
  }

  return "light";
};

const handleMenuFocusCleanup = () => {
  const focusedElements = document.querySelectorAll(`
    .MuiMenuItem-root:focus,
    .MuiMenu-root [tabindex]:focus,
    .MuiMenu-root button:focus,
    .MuiMenu-root [role="menuitem"]:focus,
    .MuiSelect-select:focus
  `);

  focusedElements.forEach((el) => {
    if (el && typeof el.blur === "function") {
      el.blur();
    }
  });
};

const accessibilityOverrides = {
  MuiSelect: {
    defaultProps: {
      MenuProps: {
        disableAutoFocusItem: true,
        disableRestoreFocus: true,
        slotProps: {
          transition: {
            onExit: handleMenuFocusCleanup,
            onExited: () => {
              setTimeout(handleMenuFocusCleanup, 50);
            },
          },
          paper: {
            onMouseDown: (e) => {
              e.preventDefault();
            },
          },
        },
      },
    },
  },
  MuiMenu: {
    defaultProps: {
      disableAutoFocusItem: true,
      disableRestoreFocus: true,
      slotProps: {
        transition: {
          onExit: handleMenuFocusCleanup,
          onExited: () => {
            setTimeout(handleMenuFocusCleanup, 50);
          },
        },
      },
    },
  },
  MuiMenuItem: {
    defaultProps: {
      disableRipple: false,
    },
    styleOverrides: {
      root: {
        "&:focus": {
          outline: "none",
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
        "&.Mui-selected:focus": {
          backgroundColor: "rgba(25, 118, 210, 0.12)",
        },
      },
    },
  },
  MuiPopover: {
    defaultProps: {
      disableRestoreFocus: true,
      slotProps: {
        transition: {
          onExit: handleMenuFocusCleanup,
        },
      },
    },
  },
  MuiModal: {
    defaultProps: {
      disableRestoreFocus: true,
    },
  },
};

export const BuildTheme = (config) => {
  const customizer = useSelector((state) => state.customizer);
  const savedTheme = getThemeFromCookie();
  const activeMode =
    customizer.loadingModeOverride || customizer.activeMode || savedTheme;

  const defaultTheme = activeMode === "dark" ? baseDarkTheme : baseLightTheme;
  const defaultShadow = activeMode === "dark" ? darkshadows : shadows;
  const themeSelect = activeMode === "dark" ? baseDarkTheme : baseLightTheme;

  const baseMode = {
    palette: {
      mode: activeMode,
    },
    shape: {
      borderRadius: customizer.borderRadius,
    },
    shadows: defaultShadow,
    typography: typography,
  };

  const theme = createTheme(
    _.merge({}, baseMode, defaultTheme, locales, themeSelect, {
      direction: config.direction,
    }),
  );

  theme.components = _.merge({}, components(theme), accessibilityOverrides);

  return theme;
};

const ThemeSettings = () => {
  const activeTheme = useSelector((state) => state.customizer.activeTheme);
  const theme = BuildTheme({
    theme: activeTheme,
  });
  return theme;
};

export { ThemeSettings };
